import './instrument'
import React from 'react'
import { createRoot } from 'react-dom/client'
import { Provider } from 'react-redux'
import { BrowserRouter } from 'react-router-dom'
import { QueryClientProvider } from '@tanstack/react-query'
import { Sentry } from './sentry'
import { Provider as ApolloProvider } from './graphql/provider'
import Auth0Provider from './components/Auth0'
import { SocketIOProvider } from './components/SocketIO'
import { msalInstance } from './config/msalConfig'

import './index.css'
import store from './redux/store'
import * as serviceWorker from './serviceWorker'
import App from './App'
import queryClient from './queryClient'

const container = document.getElementById('root')
const root = createRoot(container)

const renderApp = () => {
  root.render(
    <QueryClientProvider client={queryClient}>
      <Provider store={store}>
        <Auth0Provider>
          <ApolloProvider>
            <SocketIOProvider>
              <BrowserRouter>
                <Sentry.ErrorBoundary fallback={<p>An error has occurred. Please reload.</p>}>
                  <App />
                </Sentry.ErrorBoundary>
              </BrowserRouter>
            </SocketIOProvider>
          </ApolloProvider>
        </Auth0Provider>
      </Provider>
    </QueryClientProvider>,
  )
}

// MSAL names its auth popups "msal.*". If the SPA boots there (e.g. redirectUri
// still points at /), any loginPopup call throws block_nested_popups.
const isMsalPopup =
  typeof window !== 'undefined' &&
  !!window.opener &&
  window.opener !== window &&
  typeof window.name === 'string' &&
  window.name.indexOf('msal.') === 0

const isNestedAuthFrame =
  typeof window !== 'undefined' && window.self !== window.top && !window.Cypress

// Inside the MSAL popup, do nothing: the opener window reads the auth response
// hash via monitorPopupForHash. Booting the app or calling handleRedirectPromise
// here clears the hash and makes the opener hang until the login watchdog fires.
if (!isMsalPopup && !isNestedAuthFrame) {
  msalInstance
    .initialize()
    .then(() => msalInstance.handleRedirectPromise())
    .then(() => renderApp())
    .catch(() => renderApp())
}

// expose store when run in Cypress
if (window.Cypress) {
  window.store = store
}

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister()
